import type { FeatureFlag, Features } from '~/plugins/featureFlagPlugin';
import type { TgnFloatingNavbarCommandProps } from '~/components/TgnFloatingNavbar/TgnFloatingNavbar';

import i18n from '~modules/i18n';
import { GrowthBook } from '@growthbook/growthbook';
import { ICONS } from '~helpers/constants';
import { SESSION_STATUS_ENUM } from '~/enums/Session.enum';
import { TgnBaseMenuItemProps, TgnBreadcrumbsProps } from '~/components/TgnTypes';
import { TgnNavigationCommand } from '~/models/TgnNavigationCommand';
import { defineStore } from 'pinia';
import { isNotNull } from '@scansource-brazil/zeus-core-ui/data';
import { omit } from '~utils/omit';
import { ref } from 'vue';
import { useAuthStore } from '~stores/useAuthStore';
import { useLayoutStore } from '~stores/useLayoutStore';
import { useProfileStore } from './useProfileStore';
import { useRouter } from 'vue-router';

export const useNavigationStore = defineStore('MY_SCAN_SOURCE_NAVIGATION_STORE', () => {
  const { t } = i18n.global;
  const router = useRouter();
  const route = useRoute();

  const LAYOUT_STORE = useLayoutStore();
  const AUTH_STORE = useAuthStore();
  const PROFILE_STORE = useProfileStore();
  const { profile } = storeToRefs(PROFILE_STORE);
  const { session } = storeToRefs(AUTH_STORE);
  const companyStore = useCompanyStore();
  const { selectedCompany } = storeToRefs(companyStore);

  const featureFlag = ref<FeatureFlag | null>(null);
  // Lista base de itens do menu de navegação
  const baseMenuItems = ref<TgnBaseMenuItemProps[]>([
    {
      name: 'navigation-item-dashboard',
      icon: ICONS.DASHBOARD,
      label: t('ROUTES.LABEL.DASHBOARD'),
      itemKey: 'dashboard',
      command: () => navigationCommand('dashboard'),
      active: true,
    },
    {
      name: 'navigation-item-orders',
      icon: ICONS.ORDERS,
      label: t('ROUTES.LABEL.ORDERS'),
      itemKey: 'orders',
      command: undefined,
      active: false,
      items: [
        {
          name: 'navigation-item-orders-all',
          icon: ICONS.ORDERS,
          label: t('ROUTES.LABEL.ALL_ORDERS'),
          itemKey: 'orders',
          command: () => navigationCommand('orders'),
          active: false,
        },
        {
          name: 'navigation-item-orders-invoices',
          icon: ICONS.INVOICE,
          label: t('ROUTES.LABEL.INVOICES'),
          itemKey: 'invoices',
          command: () => navigationCommand('invoices'),
          active: false,
        },
        {
          name: 'navigation-item-orders-bills',
          icon: ICONS.BILL,
          label: t('ROUTES.LABEL.BILLS'),
          itemKey: 'payment-slips',
          command: () => navigationCommand('payment-slips'),
          active: false,
        },
      ],
    },
  ]);

  const baseMenuItemsUngrouped = ref<TgnBaseMenuItemProps[]>([
    {
      name: 'navigation-item-dashboard',
      icon: ICONS.DASHBOARD,
      label: t('ROUTES.LABEL.DASHBOARD'),
      itemKey: 'dashboard',
      command: () => navigationCommand('dashboard'),
      active: true,
    },
    {
      name: 'navigation-item-orders-all',
      icon: ICONS.ORDERS,
       label: t('ROUTES.LABEL.ORDERS'),
      itemKey: 'orders',
      command: () => navigationCommand('orders'),
      active: false,
    },
    {
      name: 'navigation-item-orders-invoices',
      icon: ICONS.INVOICE,
      label: t('ROUTES.LABEL.INVOICES'),
      itemKey: 'invoices',
      command: () => navigationCommand('invoices'),
      active: false,
    },
    {
      name: 'navigation-item-orders-bills',
      icon: ICONS.BILL,
      label: t('ROUTES.LABEL.BILLS'),
      itemKey: 'payment-slips',
      command: () => navigationCommand('payment-slips'),
      active: false,
    },
  ]);

  const commissionsMenuItems = ref({
    name: 'navigation-item-commissions',
    icon: ICONS.COMMISSIONS,
    label: t('ROUTES.LABEL.COMMISSIONS'),
    itemKey: 'commissions',
    command: () => navigationCommand('commissions'),
    active: false,
  });

  const myAccountMenuItems = ref({
    name: 'navigation-item-my-account',
    icon: ICONS.MY_ACCOUNTS,
    label: t('ROUTES.LABEL.MY_ACCOUNT'),
    itemKey: 'my-account',
    command: undefined,
    active: false,
    items: [
      {
        name: 'navigation-item-my-account-balance-statement',
        icon: ICONS.INVOICE,
        label: t('ROUTES.LABEL.BALANCE_STATEMENT'),
        itemKey: 'my-account-balance-statement',
        command: () => navigationCommand('my-account/balance-statement'),
        active: false,
      },
      {
        name: 'navigation-item-my-account-redemptions',
        icon: ICONS.BILL,
        label: t('ROUTES.LABEL.MY_REDEMPTIONS'),
        itemKey: 'my-account-redemptions',
        command: () => navigationCommand('my-account/redemptions'),
        active: false,
      },
    ],
  });

  // Inicializa os menus com base no perfil
  const navigationMenu = ref<TgnBaseMenuItemProps[]>([...baseMenuItems.value]);

  const navigationCards = computed((): TgnBaseMenuItemProps[] => {
    const output = navigationMenu.value.filter((item) => item.itemKey !== 'dashboard');

    output.push({
      icon: ICONS.USERS,
      label: 'Administrar Usuários',
      name: 'users',
      itemKey: 'users',
      command: () => navigationCommand('users'),
      active: undefined,
      type: 'link',
    } satisfies TgnBaseMenuItemProps);

    return output as TgnBaseMenuItemProps[];
  });

  const navigationCommands = ref<TgnFloatingNavbarCommandProps[]>([
    {
      name: 'select-company',
      icon: ICONS.ECONOMIC_GROUP,
      label: t('DASHBOARD.LABEL.ACTION.ECONOMIC_GROUP'),
      command: LAYOUT_STORE.showEconomicGroupModal,
      fixed: true,
    },
  ]);

  // #region BreadCrumbs
  const navigationBreadcrumbs = ref<TgnBreadcrumbsProps>({
    name: 'navbar-breadcrumbs',
    home: {
      name: 'navigation-item-dashboard',
      icon: ICONS.DASHBOARD,
      route: '/dashboard',
    },
    model: [],
  });

  function addBreadcrumbItem(breadCrumb: TgnBaseMenuItemProps) {
    navigationBreadcrumbs.value.model.push(breadCrumb);
  }

  watchEffect(() => {
    if (route.name) toggleNavigationItem(route.name.toString());
    if (route.name && route.meta.isRoot) {
      const existingIndex = navigationBreadcrumbs.value.model.findIndex(
        (item) => item.route === route.fullPath,
      );

      if (existingIndex === 0) {
        navigationBreadcrumbs.value.model = [navigationBreadcrumbs.value.model[0]];
      } else if (existingIndex === 1) {
        navigationBreadcrumbs.value.model = [
          navigationBreadcrumbs.value.model[0],
          navigationBreadcrumbs.value.model[1],
        ];
      } else {
        if (navigationBreadcrumbs.value.model.length >= 3) {
          navigationBreadcrumbs.value.model.shift();
        }
        addBreadcrumbItem({
          name: `navigation-item-${route.name.toString()}`,
          route: route.fullPath,
          label: t(`ROUTES.LABEL.${route.name.toString().toUpperCase()}`),
        });
      }
    }
  });
  // #endregion

  // #region Mobile
  const navOptions = ref<object[]>([
    {
      icon: ICONS.DASHBOARD,
      label: 'Dashboard',
      name: 'dashboard',
      action: () => redirect('dashboard'),
      active: true,
      type: 'link',
    },
    {
      icon: ICONS.ORDERS,
      label: 'Minhas Compras',
      name: 'orders',
      action: () => redirect('orders'),
      active: false,
      type: 'link',
    },
    {
      icon: ICONS.INVOICE,
      label: 'Notas Fiscais',
      name: 'invoices',
      action: () => redirect('invoices'),
      active: false,
      type: 'link',
    },
    {
      icon: ICONS.BILL,
      label: 'Boletos',
      name: 'payment-slips',
      action: () => redirect('payment-slips'),
      active: false,
      type: 'link',
    },
    {
      icon: ICONS.COMMISSIONS,
      label: 'Comissões',
      name: 'commissions',
      action: () => redirect('commissions'),
      active: false,
      type: 'link',
    },
    {
      icon: ICONS.HELP,
      label: 'Contatos',
      name: 'contacts',
      action: () => redirect('contacts'),
      active: false,
      type: 'link',
    },
    {
      icon: ICONS.MY_ACCOUNTS,
      label: 'Saldo e Extrato',
      name: 'balance-statement',
      action: () => redirect('my-account/balance-statement'),
      active: false,
      type: 'link',
    },
    {
      icon: ICONS.MY_ACCOUNTS,
      label: 'Meus Resgates',
      name: 'my-account/redemptions',
      action: () => redirect('my-account/redemptions'),
      active: false,
      type: 'link',
    },
  ]);

  const rootOptions = navOptions.value.map((option) => omit(option, ['active']));
  // #endregion

  function redirect(name: string): void {
    router.push(`/${name}`);
  }

  function addOption(option: any): void {
    navOptions.value.push(option);
  }

  function addOptions(options: any[]) {
    navOptions.value.push(...options);
  }

  function restoreOptions() {
    const result: any[] = [];

    rootOptions.forEach((option: any) => {
      const found = navOptions.value.find(
        (navOption: any) => navOption.name === option.name,
      );

      if (found) {
        result.push(found);
      }
    });

    navOptions.value = [...result];
  }

  function addNavigationCommand(command: TgnNavigationCommand) {
    const hasCommand = navigationCommands.value.some(({ name }) => command.name === name);

    if (hasCommand) return;

    navigationCommands.value.push(command);
  }

  function removeNavigationCommand(commandName: string | string[]) {
    if (Array.isArray(commandName)) {
      commandName.forEach((cName) => {
        navigationCommands.value = navigationCommands.value.filter(
          ({ name }) => name !== cName,
        );
      });

      return;
    }

    navigationCommands.value = navigationCommands.value.filter(
      ({ name }) => name !== commandName,
    );
  }

  function restoreNavigationCommands() {
    navigationCommands.value = navigationCommands.value.filter(
      (command) => command.fixed,
    );
  }

  function navigationCommand(itemKey: string): void {
    redirect(itemKey);
    toggleNavigationItem(itemKey);
  }

  /**
   * Toggles the active state of a navigation item based on its key.
   *
   * This function iterates through all navigation menu items and:
   * - For items without subitems: sets the item as active if its itemKey matches the provided key
   * - For items with subitems: sets any matching subitem as active and then marks the parent
   *   item as active if any of its children are active
   *
   * @param itemKey - The unique key identifier of the navigation item to be activated
   */
  function toggleNavigationItem(itemKey: string): void {
    navigationMenu.value = navigationMenu.value.map((item: TgnBaseMenuItemProps) => {
      if (!item.items?.length) {
        return { ...item, active: item.itemKey === itemKey };
      }

      const updatedSubItems = item.items.map((subItem) => ({
        ...subItem,
        active: subItem.itemKey === itemKey,
      }));

      return {
        ...item,
        items: updatedSubItems,
        active: updatedSubItems.some((subItem) => subItem.active),
      };
    });
  }

  function redirectOfflineUser() {
    if (session.value.sessionStatus === SESSION_STATUS_ENUM.LOGGED_OUT)
      return redirect('goodbye');
    if (session.value.sessionStatus === SESSION_STATUS_ENUM.DEFAULT)
      return redirect('welcome');
    if (session.value.sessionStatus === SESSION_STATUS_ENUM.UNAVAILABLE)
      return redirect('unavailable');
  }

  function redirectOnlineUser() {
    switch (session.value.sessionStatus) {
      case SESSION_STATUS_ENUM.NOT_ALLOWED:
        return redirect('not-allowed');
      case SESSION_STATUS_ENUM.UNAVAILABLE:
        return redirect('unavailable');
      default:
        return redirect('dashboard');
    }
  }

  function navigateBySessionStatus() {
    if (!session.value.authenticated) return redirectOfflineUser();
    return redirectOnlineUser();
  }

  /**
   * Updates the navigation menu based on feature flags.
   *
   * If the environment is not commercial and feature flags are available,
   * this function will conditionally add menu items:
   * - If 'commissions' and 'my-accounts' flags are both on, adds both menu item sets
   * - If only 'my-accounts' flag is on, adds only my account menu items
   * - If only 'commissions' flag is on, adds only commissions menu items
   *
   * No menu changes occur if feature flags are null or if in a commercial environment.
   */
function updateNavigationMenu() {
  if (isNotNull(featureFlag.value)) {
      const whiteListCommissions = featureFlag.value.getByFlagName('WhiteListCommissions');
      if (Array.isArray(whiteListCommissions.defaultValue) && companyIsOnTheList(whiteListCommissions.defaultValue)) {
        if (!profile.value.isCommercial) {
          navigationMenu.value = baseMenuItems.value.concat(
            commissionsMenuItems.value,
            myAccountMenuItems.value,
          );
        }
        return;
      } else if (featureFlag.value.isOn('CommissionsFeaturesAvailable' as keyof Features)) {
        if (!profile.value.isCommercial) {
          navigationMenu.value = baseMenuItems.value.concat(
            commissionsMenuItems.value,
            myAccountMenuItems.value,
          );
        }
      } else {
          navigationMenu.value = baseMenuItemsUngrouped.value;
      }
    }
  }


  function companyIsOnTheList (list: string[]) {
    const result = list.filter((item: string) => item === selectedCompany.value?.companyDocument.value )
    return result.length > 0
  }
  /**
   * Sets up an interval to refresh the features from the feature flag service every minute.
   *
   * This function:
   * 1. Logs when the refresh process starts
   * 2. Attempts to refresh features using the feature flag service
   * 3. Updates the navigation menu upon successful refresh
   * 4. Logs errors if the refresh fails
   *
   * @returns {number} The interval ID that can be used with clearInterval
   */
  function refreshNavigationMenu() {
    console.info(
      `🕐 ${new Date(Date.now()).toTimeString().split(' ')[0]} - Refresh feature timer started!`,
    );
    setInterval(async () => {
      console.info(`🚀 Refreshing features...`);

      await featureFlag.value
        ?.refreshFeatures()
        .then(() => {
          console.info(
            `✅ ${new Date(Date.now()).toTimeString().split(' ')[0]} - Features refreshed!`,
          );
          updateNavigationMenu();
        })
        .catch((error) =>
          console.error(
            new Error(
              `❌ ${new Date(Date.now()).toTimeString().split(' ')[0]} - Error refreshing features: ${error.message}`,
              ...error,
            ),
          ),
        );
    }, 60000);
  }

  /**
   * Sets the Fature Flag instance for feature flagging and refreshes the navigation menu.
   *
   * This function updates the feature flag reference and triggers a refresh of the
   * navigation menu to reflect any changes based on the updated feature flags.
   *
   * @param FEATURE_FLAG - The Fature Flag instance to be used for feature flag evaluation
   */
  function setFeatureFlag(FEATURE_FLAG: FeatureFlag) {
    featureFlag.value = FEATURE_FLAG;
    updateNavigationMenu();
    refreshNavigationMenu();
  }

  // #region Return:
  return {
    navOptions,
    navigationMenu,
    navigationCommands,
    navigationBreadcrumbs,
    navigationCards,
    addOption,
    addOptions,
    addNavigationCommand,
    removeNavigationCommand,
    restoreNavigationCommands,
    restoreOptions,
    redirect,
    navigateBySessionStatus,
    setFeatureFlag,
  };
  // #endregion
});
